import { useState, useEffect } from 'react';
import { useCapabilities } from '../../components/admin/CapabilityProvider';
import { apiGet, apiPost } from '../../services/apiClient.js';
import { createIntent } from '../../services/intentService.js';
import DestructiveActionConfirm from '../../components/admin/DestructiveActionConfirm.jsx';
import NotAuthorized from '../../components/admin/NotAuthorized.jsx';
import AdminPageHeader from '../../components/admin/ui/AdminPageHeader';
import AdminCard from '../../components/admin/ui/AdminCard';
import AdminButton from '../../components/admin/ui/AdminButton';
import AdminStatusBadge from '../../components/admin/ui/AdminStatusBadge';
import AdminAlert from '../../components/admin/ui/AdminAlert';

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showKillSwitchConfirm, setShowKillSwitchConfirm] = useState(false);
  const [killSwitchAction, setKillSwitchAction] = useState(null); // 'enable' or 'disable'
  const { has } = useCapabilities();

  const canManageIntegrations = has('admin:integrations:manage');
  const canReadIntegrations = has('admin:integrations:read');

  useEffect(() => {
    if (!canReadIntegrations) {
      setLoading(false);
      return;
    }
    loadSettings();
  }, [canReadIntegrations]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch kill switch status
      const killSwitchResponse = await apiGet('/admin/integrations/global/status').catch(() => ({
        globally_disabled: false,
      }));

      // For mutation freeze and egress, these are environment variables
      // In a real implementation, we'd have an endpoint to fetch these
      // For now, we'll use mock data (these would come from a system settings endpoint)
      const mockSettings = {
        mutationFreezeEnabled: false, // Would come from ADMIN_MUTATIONS_ENABLED env var
        egressEnabled: true, // Would come from INTEGRATIONS_EGRESS_ENABLED env var
      };

      setSettings({
        killSwitch: {
          disabled: killSwitchResponse.globally_disabled || false,
          updatedAt: killSwitchResponse.updated_at || null,
          updatedBy: killSwitchResponse.updated_by || null,
          reason: killSwitchResponse.reason || null,
        },
        mutationFreeze: {
          enabled: mockSettings.mutationFreezeEnabled,
        },
        egress: {
          enabled: mockSettings.egressEnabled,
        },
      });
    } catch (err) {
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleKillSwitchToggle = (action) => {
    setKillSwitchAction(action);
    setShowKillSwitchConfirm(true);
  };

  const handleKillSwitchConfirm = async (reason) => {
    try {
      setError(null);

      // Create intent first
      const intentAction = killSwitchAction === 'enable'
        ? 'global_kill_switch.enable'
        : 'global_kill_switch.disable';
      
      const intentResponse = await createIntent({
        action: intentAction,
        targetType: 'system',
        targetId: 'global_kill_switch',
        reason: reason.trim(),
      });

      const intentAuditEventId = intentResponse.intentAuditEventId;

      // Execute the mutation with intent
      const endpoint = killSwitchAction === 'enable'
        ? '/admin/integrations/global/enable'
        : '/admin/integrations/global/disable';

      await apiPost(endpoint, {
        reason: reason.trim(),
        intentAuditEventId,
      });

      setShowKillSwitchConfirm(false);
      setKillSwitchAction(null);
      // Reload settings to reflect changes
      await loadSettings();
    } catch (err) {
      setError(err.message || `Failed to ${killSwitchAction} kill switch`);
    }
  };

  const handleKillSwitchCancel = () => {
    setShowKillSwitchConfirm(false);
    setKillSwitchAction(null);
  };

  if (!canReadIntegrations) {
    return <NotAuthorized requiredCapability="admin:integrations:read" />;
  }

  if (loading) {
    return <div className="admin-loading">Loading settings...</div>;
  }

  if (error && !settings) {
    return (
      <div role="alert" aria-live="polite">
        <AdminAlert type="error" message={`Error: ${error}`} dismissible={false} />
        <AdminButton onClick={loadSettings} ariaLabel="Retry loading settings">
          Retry
        </AdminButton>
      </div>
    );
  }

  if (!settings) {
    return <div className="admin-empty-state">No settings available</div>;
  }

  return (
    <div>
      <AdminPageHeader title="Settings" />

      {/* Error banner */}
      {error && (
        <AdminAlert type="error" message={error} dismissible={false} />
      )}

      {/* Global Kill Switch */}
      <AdminCard
        title="Global Kill Switch"
        actions={
          canManageIntegrations ? (
            settings.killSwitch.disabled ? (
              <AdminButton
                variant="primary"
                onClick={() => handleKillSwitchToggle('enable')}
                ariaLabel="Enable all integrations"
              >
                Enable All Integrations
              </AdminButton>
            ) : (
              <AdminButton
                variant="destructive"
                onClick={() => handleKillSwitchToggle('disable')}
                ariaLabel="Disable all integrations (global kill switch)"
              >
                Disable All Integrations
              </AdminButton>
            )
          ) : null
        }
      >
        <div className="admin-form-group">
          <div className="admin-form-label">Status:</div>
          <div>
            <AdminStatusBadge status={settings.killSwitch.disabled ? 'inactive' : 'active'}>
              {settings.killSwitch.disabled ? 'Active (All Disabled)' : 'Inactive (All Enabled)'}
            </AdminStatusBadge>
          </div>
        </div>
        {settings.killSwitch.reason && (
          <div className="admin-form-group">
            <div className="admin-form-label">Reason:</div>
            <div>{settings.killSwitch.reason}</div>
          </div>
        )}
        {settings.killSwitch.updatedAt && (
          <div className="admin-form-group">
            <div className="admin-form-label">Last Updated:</div>
            <div>{new Date(settings.killSwitch.updatedAt).toLocaleString()}</div>
          </div>
        )}
        {settings.killSwitch.updatedBy && (
          <div className="admin-form-group">
            <div className="admin-form-label">Updated By:</div>
            <div>
              <code style={{ fontFamily: 'var(--font-family-monospace)', fontSize: 'var(--font-size-sm)' }}>{settings.killSwitch.updatedBy}</code>
            </div>
          </div>
        )}
        {!canManageIntegrations && (
          <div className="admin-text-tertiary" style={{ fontStyle: 'italic', marginTop: 'var(--spacing-4)' }}>
            You do not have permission to modify this setting.
          </div>
        )}
      </AdminCard>

      {/* Mutation Freeze */}
      <AdminCard title="Mutation Freeze">
        <div className="admin-form-group">
          <div className="admin-form-label">Status:</div>
          <div>
            <AdminStatusBadge status={settings.mutationFreeze.enabled ? 'inactive' : 'active'}>
              {settings.mutationFreeze.enabled ? 'Frozen (Mutations Blocked)' : 'Active (Mutations Allowed)'}
            </AdminStatusBadge>
          </div>
        </div>
        <div className="admin-text-tertiary" style={{ fontStyle: 'italic', marginTop: 'var(--spacing-4)' }}>
          This setting is controlled by the ADMIN_MUTATIONS_ENABLED environment variable.
        </div>
      </AdminCard>

      {/* Egress Enabled */}
      <AdminCard title="Integration Egress">
        <div className="admin-form-group">
          <div className="admin-form-label">Status:</div>
          <div>
            <AdminStatusBadge status={settings.egress.enabled ? 'active' : 'inactive'}>
              {settings.egress.enabled ? 'Enabled' : 'Disabled'}
            </AdminStatusBadge>
          </div>
        </div>
        <div className="admin-text-tertiary" style={{ fontStyle: 'italic', marginTop: 'var(--spacing-4)' }}>
          This setting is controlled by the INTEGRATIONS_EGRESS_ENABLED environment variable.
        </div>
      </AdminCard>

      {/* Confirmation Modal */}
      <DestructiveActionConfirm
        open={showKillSwitchConfirm}
        onClose={handleKillSwitchCancel}
        onConfirm={handleKillSwitchConfirm}
        title={
          killSwitchAction === 'enable'
            ? 'Enable All Integrations'
            : 'Disable All Integrations (Global Kill Switch)'
        }
        description={
          killSwitchAction === 'enable'
            ? 'This will enable all integrations. Are you sure?'
            : 'This will disable ALL integrations globally. This is a critical action that affects all integration operations. Are you sure?'
        }
        confirmLabel={killSwitchAction === 'enable' ? 'Enable All' : 'Disable All'}
      />
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
  },
  header: {
    fontSize: '2rem',
    marginBottom: '1.5rem',
    color: '#111827',
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#6b7280',
  },
  error: {
    padding: '2rem',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '0.5rem',
  },
  errorBanner: {
    padding: '1rem',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '0.5rem',
    marginBottom: '1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  empty: {
    padding: '2rem',
    textAlign: 'center',
    color: '#6b7280',
  },
  button: {
    padding: '0.5rem 1rem',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'background-color 0.2s',
    outline: 'none',
  },
  buttonFocus: {
    outline: '2px solid #fff',
    outlineOffset: '2px',
  },
  buttonSmall: {
    padding: '0.375rem 0.75rem',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: '500',
  },
  buttonDanger: {
    backgroundColor: '#ef4444',
  },
  section: {
    marginBottom: '2rem',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    color: '#111827',
    margin: 0,
  },
  sectionActions: {
    display: 'flex',
    gap: '0.5rem',
  },
  card: {
    backgroundColor: '#fff',
    padding: '1.5rem',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  settingRow: {
    display: 'flex',
    padding: '0.75rem 0',
    borderBottom: '1px solid #e5e7eb',
  },
  settingLabel: {
    width: '200px',
    fontWeight: '500',
    color: '#374151',
    fontSize: '0.875rem',
  },
  settingValue: {
    flex: 1,
    color: '#111827',
    fontSize: '0.875rem',
  },
  badge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.875rem',
    fontWeight: '500',
    display: 'inline-block',
    color: '#fff',
  },
  code: {
    backgroundColor: '#f3f4f6',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    fontSize: '0.875rem',
    fontFamily: 'monospace',
  },
  readOnlyNote: {
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#f3f4f6',
    borderRadius: '0.375rem',
    color: '#6b7280',
    fontSize: '0.875rem',
  },
};
